import { useEffect, useRef, useCallback } from 'react';
import { createFaceRecognitionTools, validateFace } from '../util/faceValidation.js';

/**
 * Hook that initializes FaceDetector + FaceLandmarker and exposes validateFace.
 * Tools are created once and cleaned up on unmount.
 * @returns {{ validateFace: (blob: Blob, imageWidth: number, imageHeight: number) => Promise<{accepted: boolean, reason?: string, faceBoundingBox?: Object}> }}
 */
export function useFaceValidator() {
	const toolsRef = useRef(null);
	const initPromiseRef = useRef(null);

	useEffect(() => {
		let cancelled = false;
		initPromiseRef.current = createFaceRecognitionTools()
			.then((tools) => {
				if (!cancelled) {
					toolsRef.current = tools;
				}
				return tools;
			})
			.catch((err) => {
				console.error('Failed to initialize face recognition tools:', err);
				return null;
			});

		return () => {
			cancelled = true;
			toolsRef.current = null;
			initPromiseRef.current = null;
		};
	}, []);

	const validate = useCallback(async (blob, imageWidth, imageHeight) => {
		if (!blob) {
			return { accepted: false, reason: 'camNoFace' };
		}
		if (!toolsRef.current) {
			await initPromiseRef.current;
		}
		if (!toolsRef.current) {
			return { accepted: false, reason: 'camNoFace' };
		}
		return validateFace(blob, toolsRef.current, imageWidth, imageHeight);
	}, []);

	return { validateFace: validate };
}
